import { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  ProgressBar,
  Banner,
  Badge,
  Divider,
  Select,
  Spinner
} from '@shopify/polaris';

const STEPS = [
  { title: 'Welcome', description: 'Start finding profit leaks' },
  { title: 'Add Costs', description: 'Unlock true order-level profit' },
  { title: 'Set Goal', description: 'Choose your margin guardrail' },
  { title: 'Your Snapshot', description: 'See your first margin-protection view' }
];

const PROFIT_TARGETS = [
  { value: '15', label: '15% - Getting started' },
  { value: '25', label: '25% - Healthy business' },
  { value: '35', label: '35% - Strong growth' },
  { value: '50', label: '50% - Premium brand' }
];

const SAMPLE_LEAK_SNAPSHOT = [
  { label: 'Sample order revenue', value: '$79.00', detail: 'After Shopify discounts' },
  { label: 'COGS entered', value: '$54.00', detail: 'Product cost coverage' },
  { label: 'Fees and shipping pressure', value: '$28.40', detail: 'Operational margin drag' },
  { label: 'True net profit', value: '-$3.40', detail: 'Negative-margin order' }
];

export default function Onboarding({ shop, onComplete, showToast }) {
  const [step, setStep] = useState(0);
  const [products, setProducts] = useState([]);
  const [productCosts, setProductCosts] = useState({});
  const [profitTarget, setProfitTarget] = useState('25');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [snapshot, setSnapshot] = useState(null);

  // Load top products from Shopify (sync first if needed)
  useEffect(() => {
    if (!shop || step !== 1) return;

    const loadProducts = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // First try to load products directly
        let response = await fetch(`/api/products?shop=${shop}`);
        let data;

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          const errMsg = errData.message || errData.error || `Server error (${response.status})`;

          // If access token is missing/expired, show specific message
          if (response.status === 401 || errData.needsReinstall) {
            setLoadError('Store authentication expired. Please reinstall the app from your Shopify admin.');
            setProducts([]);
            return;
          }

          console.error('Products API error:', response.status, errMsg);
          // Try sync as fallback
          data = [];
        } else {
          data = await response.json();
        }

        // If no products, trigger a sync and try again
        if (Array.isArray(data) && data.length === 0) {
          try {
            const syncResponse = await fetch('/api/sync', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ shop })
            });
            if (syncResponse.ok) {
              // Retry loading products after sync
              response = await fetch(`/api/products?shop=${shop}`);
              if (response.ok) {
                data = await response.json();
              } else {
                const errData = await response.json().catch(() => ({}));
                setLoadError(errData.message || errData.error || 'Failed to load products after sync');
                setProducts([]);
                return;
              }
            } else {
              const syncErr = await syncResponse.json().catch(() => ({}));
              console.error('Sync failed:', syncResponse.status, syncErr);
              if (syncResponse.status === 401 || syncErr.needsReinstall) {
                setLoadError('Store authentication expired. Please reinstall the app from your Shopify admin.');
                setProducts([]);
                return;
              }
            }
          } catch (syncErr) {
            console.error('Auto-sync failed:', syncErr);
          }
        }

        // Validate response is an array
        if (!Array.isArray(data)) {
          console.error('Invalid products response:', data);
          setLoadError('Unexpected response from server. Please try again.');
          setProducts([]);
          return;
        }

        if (data.length === 0) {
          setLoadError(null); // No error — store genuinely has no products
          // Auto-skip to goal step if store has no products
          setProducts([]);
          setStep(2);
          return;
        }

        // Show top 5 products by price (most impactful for profit)
        const sorted = data
          .filter(p => {
            if (!p) return false;
            return p.variantTitle === 'Default Title' ||
                   data.filter(d => d && d.productId === p.productId).length === 1;
          })
          .sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0))
          .slice(0, 5);
        setProducts(sorted);
      } catch (error) {
        console.error('Failed to load products:', error);
        setLoadError('Could not connect to server. Please check your connection and try again.');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [shop, step, retryCount]);

  // Calculate snapshot when reaching step 3
  useEffect(() => {
    if (step !== 3) return;

    try {
      localStorage.setItem('profitpulse_onboarding_snapshot_seen', new Date().toISOString());
    } catch {
      // Local storage is only used for non-critical funnel memory.
    }

    const productsWithCosts = products.filter(p => productCosts[p.variantId]);
    const totalRevenue = productsWithCosts.reduce((sum, p) => sum + parseFloat(p.price), 0);
    const totalCost = productsWithCosts.reduce((sum, p) => sum + parseFloat(productCosts[p.variantId] || 0), 0);
    const currentMargin = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue * 100) : 0;
    const targetMargin = parseInt(profitTarget);
    const potentialMonthlyRevenue = totalRevenue * 30; // rough estimate
    const currentProfit = potentialMonthlyRevenue * (currentMargin / 100);
    const targetProfit = potentialMonthlyRevenue * (targetMargin / 100);
    const profitGap = targetProfit - currentProfit;

    setSnapshot({
      productsAnalyzed: productsWithCosts.length,
      totalProducts: products.length,
      currentMargin: currentMargin.toFixed(1),
      targetMargin: targetMargin,
      monthlyRevenue: potentialMonthlyRevenue,
      currentProfit: currentProfit,
      targetProfit: targetProfit,
      profitGap: profitGap,
      costsCovered: productsWithCosts.length
    });
  }, [step, products, productCosts, profitTarget]);

  const handleSaveCosts = async () => {
    if (!shop) {
      showToast('Cannot save costs: shop parameter missing', true);
      return;
    }

    setLoading(true);
    try {
      const entries = Object.entries(productCosts).filter(([_, cost]) => cost && parseFloat(cost) > 0);
      let savedCount = 0;
      
      for (const [variantId, cost] of entries) {
        try {
          const response = await fetch('/api/products/cost', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              shop, 
              variantId: parseInt(variantId), 
              cost: parseFloat(cost) 
            })
          });
          
          if (response.ok) {
            savedCount++;
          } else {
            console.error('Failed to save cost for variant:', variantId);
          }
        } catch (err) {
          console.error('Error saving cost for variant:', variantId, err);
        }
      }
      
      if (savedCount > 0) {
        showToast(`Saved costs for ${savedCount} product(s)`);
      } else {
        showToast('No costs were saved', true);
      }
    } catch (error) {
      console.error('Save costs error:', error);
      showToast('Failed to save costs', true);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoal = async () => {
    if (!shop) {
      console.error('Cannot save goal: shop parameter missing');
      return;
    }

    try {
      const response = await fetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop,
          settings: {
            profit_target_percent: parseInt(profitTarget),
            onboarding_completed: true,
            onboarding_completed_at: new Date().toISOString()
          }
        })
      });
      
      if (!response.ok) {
        console.error('Failed to save goal, status:', response.status);
      }
    } catch (error) {
      console.error('Failed to save goal:', error);
    }
  };

  const handleComplete = async () => {
    await handleSaveGoal();
    onComplete();
  };

  const handleNext = async () => {
    if (step === 1 && Object.keys(productCosts).length > 0) {
      await handleSaveCosts();
    }
    setStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value || 0);
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <Page>
      <BlockStack gap="600">
        {/* Progress */}
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between">
              <Text variant="headingSm" as="h3">
                Step {step + 1} of {STEPS.length}: {STEPS[step].title}
              </Text>
              <Text variant="bodySm" as="span" tone="subdued">
                {STEPS[step].description}
              </Text>
            </InlineStack>
            <ProgressBar progress={progress} size="small" tone="primary" />
          </BlockStack>
        </Card>

        {/* Step 0: Welcome */}
        {step === 0 && (
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="400">
                <Text variant="heading2xl" as="h1">
                  Welcome to Profit Pulse AI
                </Text>
                <Text variant="headingMd" as="h2" tone="subdued">
                  Find profit leaks before they cost you money. Let&apos;s set up true profit detection in about 60 seconds.
                </Text>
                <Divider />
                <Text variant="headingMd" as="h2">Here's what we'll do:</Text>
                <BlockStack gap="300">
                  <InlineStack gap="300" blockAlign="center">
                    <Badge tone="success">1</Badge>
                    <BlockStack gap="100">
                      <Text variant="headingSm" as="h3">Enter your product costs</Text>
                      <Text variant="bodySm" as="p" tone="subdued">Unlock true profit per order after COGS, shipping, fees, and discounts</Text>
                    </BlockStack>
                  </InlineStack>
                  <InlineStack gap="300" blockAlign="center">
                    <Badge tone="success">2</Badge>
                    <BlockStack gap="100">
                      <Text variant="headingSm" as="h3">Set your margin target</Text>
                      <Text variant="bodySm" as="p" tone="subdued">We&apos;ll alert you when products or orders drift below it</Text>
                    </BlockStack>
                  </InlineStack>
                  <InlineStack gap="300" blockAlign="center">
                    <Badge tone="success">3</Badge>
                    <BlockStack gap="100">
                      <Text variant="headingSm" as="h3">See your first leak snapshot</Text>
                      <Text variant="bodySm" as="p" tone="subdued">Instant visibility into low-margin products, negative orders, and next actions</Text>
                    </BlockStack>
                  </InlineStack>
                </BlockStack>
                <Divider />
                <Banner tone="info">
                  <p>Your data stays privacy-forward. Recommendation summaries run in your browser when supported, and the default experience avoids sending store data to external AI APIs.</p>
                </Banner>
              </BlockStack>
            </Card>
            <InlineStack align="end">
              <Button variant="primary" size="large" onClick={handleNext}>
                Start Protecting Margin
              </Button>
            </InlineStack>
          </BlockStack>
        )}

        {/* Step 1: Enter Costs */}
        {step === 1 && (
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="400">
                <Text variant="headingXl" as="h1">
                  What do these products cost you?
                </Text>
                <Text variant="bodyMd" as="p" tone="subdued">
                  Enter what you pay suppliers for your top products.
                  This is the fastest way to unlock true profit per order.
                </Text>
                <Divider />
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Spinner size="large" />
                    <Text as="p" tone="subdued">Loading your products from Shopify...</Text>
                  </div>
                ) : loadError ? (
                  <BlockStack gap="300">
                    <Banner tone="critical">
                      <p>{loadError}</p>
                    </Banner>
                    <InlineStack align="center">
                      <Button onClick={() => setRetryCount(c => c + 1)} variant="primary">
                        Retry Loading Products
                      </Button>
                    </InlineStack>
                  </BlockStack>
                ) : products.length === 0 ? (
                  <Banner tone="warning">
                    <p>No products found in your store. Please add at least one product in your Shopify admin, then come back to this step. You can also skip this step and add costs later from the Products page.</p>
                  </Banner>
                ) : (
                  <BlockStack gap="400">
                    {products.map((product) => (
                      <Card key={product.variantId}>
                        <InlineStack align="space-between" blockAlign="center" gap="400" wrap={true}>
                          <BlockStack gap="100">
                            <Text variant="headingSm" as="h3">{product.productTitle}</Text>
                            <Text variant="bodySm" as="p" tone="subdued">
                              Sells for {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(product.price)}
                            </Text>
                          </BlockStack>
                          <div style={{ minWidth: '160px' }}>
                            <TextField
                              label="Your cost"
                              labelHidden
                              type="number"
                              prefix="$"
                              placeholder="0.00"
                              value={productCosts[product.variantId] || ''}
                              onChange={(val) => setProductCosts(prev => ({ ...prev, [product.variantId]: val }))}
                              autoComplete="off"
                              step="0.01"
                              min="0"
                            />
                          </div>
                          {productCosts[product.variantId] && parseFloat(productCosts[product.variantId]) > 0 && (
                            <Badge tone={
                              ((parseFloat(product.price) - parseFloat(productCosts[product.variantId])) / parseFloat(product.price) * 100) > 25
                                ? 'success' : ((parseFloat(product.price) - parseFloat(productCosts[product.variantId])) / parseFloat(product.price) * 100) > 0
                                  ? 'warning' : 'critical'
                            }>
                              {((parseFloat(product.price) - parseFloat(productCosts[product.variantId])) / parseFloat(product.price) * 100).toFixed(0)}% margin
                            </Badge>
                          )}
                        </InlineStack>
                      </Card>
                    ))}
                    <Banner tone="info">
                      <p>Don&apos;t have exact costs yet? Estimate for now and refine them later from the Products page.</p>
                    </Banner>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
            <InlineStack align="space-between">
              <Button onClick={() => setStep(0)}>Back</Button>
              <InlineStack gap="200">
                <Button onClick={() => setStep(2)}>Skip for now</Button>
                <Button variant="primary" onClick={handleNext} loading={loading}
                  disabled={Object.values(productCosts).filter(v => v && parseFloat(v) > 0).length === 0}>
                  Save & Continue
                </Button>
              </InlineStack>
            </InlineStack>
          </BlockStack>
        )}

        {/* Step 2: Set Profit Target */}
        {step === 2 && (
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="400">
                <Text variant="headingXl" as="h1">
                  What margin do you want to protect?
                </Text>
                <Text variant="bodyMd" as="p" tone="subdued">
                  We&apos;ll compare every order and product against this target so you can see when profit leaks start to open up.
                </Text>
                <Divider />
                <Select
                  label="Target profit margin"
                  options={PROFIT_TARGETS}
                  value={profitTarget}
                  onChange={setProfitTarget}
                  helpText="This is the net margin after COGS, shipping, fees, and discounts"
                />
                <BlockStack gap="300">
                  {PROFIT_TARGETS.map((target) => (
                    <Card key={target.value} background={profitTarget === target.value ? 'bg-surface-success' : 'bg-surface'}>
                      <InlineStack align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                          <Text variant="headingSm" as="h3">
                            {target.value}% margin
                          </Text>
                          <Text variant="bodySm" as="p" tone="subdued">
                            {target.value === '15' && 'Good starting point for most stores'}
                            {target.value === '25' && 'Industry standard for healthy e-commerce'}
                            {target.value === '35' && 'Strong margins indicate pricing power'}
                            {target.value === '50' && 'Premium brands and digital products'}
                          </Text>
                        </BlockStack>
                        {profitTarget === target.value && (
                          <Badge tone="success">Selected</Badge>
                        )}
                      </InlineStack>
                    </Card>
                  ))}
                </BlockStack>
              </BlockStack>
            </Card>
            <InlineStack align="space-between">
              <Button onClick={() => setStep(1)}>Back</Button>
              <Button variant="primary" onClick={handleNext}>
                Set Target & See Results
              </Button>
            </InlineStack>
          </BlockStack>
        )}

        {/* Step 3: Profit Snapshot */}
        {step === 3 && (
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="400">
                <Text variant="headingXl" as="h1">
                  Your Margin Protection Snapshot
                </Text>
                {snapshot && snapshot.costsCovered > 0 ? (
                  <BlockStack gap="400">
                    <InlineStack gap="400" wrap={true}>
                      <Card>
                        <BlockStack gap="200">
                          <Text variant="headingSm" as="h3" tone="subdued">Current Margin</Text>
                          <Text variant="heading2xl" as="p" tone={parseFloat(snapshot.currentMargin) >= parseInt(profitTarget) ? 'success' : 'warning'}>
                            {snapshot.currentMargin}%
                          </Text>
                        </BlockStack>
                      </Card>
                      <Card>
                        <BlockStack gap="200">
                          <Text variant="headingSm" as="h3" tone="subdued">Target Margin</Text>
                          <Text variant="heading2xl" as="p">{snapshot.targetMargin}%</Text>
                        </BlockStack>
                      </Card>
                      <Card>
                        <BlockStack gap="200">
                          <Text variant="headingSm" as="h3" tone="subdued">Products Analyzed</Text>
                          <Text variant="heading2xl" as="p">{snapshot.costsCovered}</Text>
                        </BlockStack>
                      </Card>
                    </InlineStack>

                    {parseFloat(snapshot.currentMargin) < parseInt(profitTarget) ? (
                      <Banner title="Profit Leak Opportunity Detected" tone="warning">
                        <p>
                          Your current margin ({snapshot.currentMargin}%) is below your {snapshot.targetMargin}% target.
                          Profit Pulse AI will now monitor every order for negative margins, discount drag, and shipping pressure automatically.
                          {snapshot.profitGap > 0 && (
                            <strong> Closing this gap could mean about {formatCurrency(snapshot.profitGap)}/month in recovered profit.</strong>
                          )}
                        </p>
                      </Banner>
                    ) : (
                      <Banner title="Great margins!" tone="success">
                        <p>
                          Your current margin ({snapshot.currentMargin}%) already exceeds your {snapshot.targetMargin}% target.
                          Profit Pulse AI will help you protect it as discounts, shipping, and product costs change.
                        </p>
                      </Banner>
                    )}
                  </BlockStack>
                ) : (
                  <BlockStack gap="400">
                    <Banner title="Preview with sample data" tone="info">
                      <p>
                        Add product costs to turn this into your live Shopify snapshot. For now, here is exactly what Profit Pulse AI will flag once COGS coverage is in place.
                      </p>
                    </Banner>

                    <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                      {SAMPLE_LEAK_SNAPSHOT.map((item) => (
                        <div
                          key={item.label}
                          style={{
                            border: '1px solid var(--p-color-border-secondary, #dfe3e8)',
                            borderRadius: '8px',
                            padding: '16px',
                            background: 'var(--p-color-bg-surface-secondary, #f6f6f7)'
                          }}
                        >
                          <BlockStack gap="100">
                            <Text variant="bodySm" as="p" tone="subdued">{item.label}</Text>
                            <Text variant="headingLg" as="p" tone={item.value.startsWith('-') ? 'critical' : undefined}>
                              {item.value}
                            </Text>
                            <Text variant="bodySm" as="p" tone="subdued">{item.detail}</Text>
                          </BlockStack>
                        </div>
                      ))}
                    </div>

                    <Banner title="First action to unlock live results" tone="warning">
                      <p>
                        Add costs for your highest-revenue products first. That unlocks true profit per order, negative-margin alerts, and low-margin product recommendations on the dashboard.
                      </p>
                    </Banner>
                  </BlockStack>
                )}

                <Divider />

                <BlockStack gap="300">
                  <Text variant="headingMd" as="h2">What happens next:</Text>
                  <InlineStack gap="300" blockAlign="center">
                    <Badge tone="success">Auto</Badge>
                    <Text as="p">Every new order is analyzed for true profit automatically via webhooks</Text>
                  </InlineStack>
                  <InlineStack gap="300" blockAlign="center">
                    <Badge tone="success">Auto</Badge>
                    <Text as="p">Negative-margin orders, discount leakage, and shipping drag trigger alerts</Text>
                  </InlineStack>
                  <InlineStack gap="300" blockAlign="center">
                    <Badge tone="success">Auto</Badge>
                    <Text as="p">AI recommendations update in-browser as your data grows</Text>
                  </InlineStack>
                  <InlineStack gap="300" blockAlign="center">
                    <Badge tone="info">Action</Badge>
                    <Text as="p">Add costs for the remaining products in the Products page</Text>
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            </Card>

            <InlineStack align="space-between">
              <Button onClick={() => setStep(2)}>Back</Button>
              <Button variant="primary" size="large" onClick={handleComplete}>
                Go to Dashboard
              </Button>
            </InlineStack>
          </BlockStack>
        )}
      </BlockStack>
    </Page>
  );
}
